import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Tooltip, Typography } from '@mui/material';
import { LuChevronLeft, LuChevronRight, LuInfo, LuMenu } from 'react-icons/lu';
import { toast } from 'react-toastify';
import { API } from '../../../../../api';
import { events, attributeKeys } from '../../../../../utils/amplitude';
import { trackWithBaseProps } from '../../../../../utils/amplitudeProductTracking';
import { useAppSelector } from '../../../../../redux/hooks';
import { getUserDetails } from '../../../../../utils/orgName';
import { getSeenNewLessons, markNewLessonSeen } from '../../../../../utils/newLessonsSeen';
import useProgramWhatsNew from '../../../../../hooks/useProgramWhatsNew';
import WhatsNewModal from '../../../../../components/course-versioning/WhatsNewModal';
import { elliottTokens as t, LessonStepKey } from './tokens';
import dosenLogo from '../../../../../assets/images/brand/dosen/white-logo.svg';
import dosenFavicon from '../../../../../assets/images/brand/dosen/favicon.png';
import LeftNav, { NavModule } from './components/LeftNav';
import StepStepper, { DEFAULT_STEPS, Step } from './components/StepStepper';
import InputBar from './components/InputBar';
import ElliottMessage, { messageBodySx } from './components/ElliottMessage';
import LearnerLessonVideo from '../../../../../components/video-builder/LearnerLessonVideo';
import useLessonVideoBuilder from '../../../../../hooks/useLessonVideoBuilder';
import StreamingText from './components/StreamingText';
import StreamingLessonContent from './components/StreamingLessonContent';
import TakeawayCard from './components/TakeawayCard';
import QuickReplyPill from './components/QuickReplyPill';
import CaseStudyCard, { CaseStudySection } from './components/CaseStudyCard';
import LessonMaterialCard, { LessonMaterial } from './components/LessonMaterialCard';
import LearnerMessage from './components/LearnerMessage';
import TypingDots from './components/TypingDots';
import QuizCard, { QuizOption, QuizCheckResult } from './components/QuizCard';
import CelebrationModal from './components/CelebrationModal';
import ImprovePerformanceModal, { ImproveItem } from './components/ImprovePerformanceModal';
import LessonCompleteStrip from './components/LessonCompleteStrip';
import { useLearnerGamification } from '../../../../../components/gamification/useLearnerGamification';
import { isElliottVoiceEnabled } from '../../../../../utils/elliottVoiceFlag';
import { isVoiceConversationSupported } from './voice/voiceEngine';
import { useVoiceConversation } from './voice/useVoiceConversation';
import { Bone } from '../../../../../components/ui/DashboardSkeletons';
import CapabilityChip from '../../../../../components/ui/CapabilityChip';
import VoiceBar from './voice/VoiceBar';
import { lessonForLearner, readOnlyRefusal, withoutAssessments } from './lessonAccess';
import { audienceRequirementsBackend } from '../../Program/components/program tabs/ProgramSettings/requirements/flags';
import { MOBILE_SX, useIsMobileApp } from '../../../../../mobile/mobileApp';

// ─── Live data shapes (GET /elliott-lesson/{grpId}/{levelId}/{taskId}) ────────

interface ElliottCaseStudy {
  id: string;
  title: string;
  sections: CaseStudySection[];
}
interface ElliottQuizQuestion {
  id: string;
  question: string;
  options: QuizOption[];
}
interface ElliottLessonPayload {
  breadcrumb: { programName: string; moduleName: string; lessonName: string };
  /** The capability this lesson builds, by name; null for a lesson that builds none. */
  capabilityName?: string | null;
  content: string;
  takeaway: string;
  applicationIntro: string[];
  caseStudies: ElliottCaseStudy[];
  /** Withheld-answer MCQ behaviour is on for this program (L-1a gate) */
  mcqRetakeMode?: boolean;
  simulation: {
    brief: string;
    skill: string;
    maxAttempts: number;
    /** Attempts used in the CURRENT round */
    attemptsUsed: number;
    /** Round the record is in (1 = in-lesson; 2+ = Improve Performance retakes) */
    round: number;
    finalPassed: boolean;
    exhausted: boolean;
    turns: Array<{
      attemptNumber: number;
      learnerResponse: string;
      coaching: string;
      passed: boolean;
      round?: number;
    }>;
    /** Elliott's persisted retake-round openers, for mid-round rehydration */
    retakeIntros?: Array<{ round: number; intro: string }>;
  } | null;
  quiz: ElliottQuizQuestion[];
  /** This learner's recorded quiz answers (latest state per question) — server-side
   *  resume. Reveal fields are withheld while a question is wrong in retake mode. */
  quizChecks?: Array<{
    questionId: string;
    answer: string;
    correct: boolean;
    correctAnswer: string | null;
    explanation: string;
    chosenReasoning?: string;
    optionReasonings?: string[];
    attempts?: number;
  }>;
  nav: { modules: NavModule[] };
  /**
   * Course versioning: the parts of this lesson a publish added or changed
   * since this learner last completed it (cleared with the lesson's badge).
   * Absent on BE builds that predate it.
   */
  partBadges?: Partial<Record<'content' | 'caseStudies' | 'liveScenario' | 'quiz', 'new' | 'updated'>>;
  /**
   * Audience Requirements (DA-5056): what this learner must do on the lesson.
   * "readOnly" is the Lesson Body, case studies and video: the Roleplay and the
   * Quiz are for others, and the page takes the lesson without them while the
   * backend switch is on (./lessonAccess.ts). Absent on BE builds before it.
   */
  access?: 'readAssess' | 'readOnly';
  /** The group they are in, or null (no groups, staff, or no longer matched). */
  audienceGroup?: 'readAssess' | 'readOnly' | null;
}

/** Lesson part (BE `partBadges` key) → the stepper step that shows it. */
const PART_STEP: Record<string, LessonStepKey> = {
  content: 'content',
  caseStudies: 'application',
  liveScenario: 'simulation',
  quiz: 'quiz'
};

// ─── Mid-lesson resume ────────────────────────────────────────────────────────
// The simulation transcript is server-persisted (per-attempt records); the
// lighter gates (current step, opened case studies, quiz answers) are kept in
// localStorage so a refresh/back-navigation drops the learner exactly where
// they left off, with prior messages rendered instantly (no re-streaming).

interface StoredQuizEntry {
  selectedId: string;
  result: QuizCheckResult;
}
interface StoredProgress {
  step?: number;
  read?: string[];
  quiz?: Record<string, StoredQuizEntry>;
}

const progressKey = (groupId: string, levelId: string, taskId: string): string =>
  `elliott-progress:${groupId}:${levelId}:${taskId}`;

const loadStoredProgress = (key: string): StoredProgress => {
  try {
    return JSON.parse(window.localStorage.getItem(key) || '{}') as StoredProgress;
  } catch {
    return {};
  }
};

const PATHWAY_LABEL = 'Dashboard';
const PATHWAY_ROUTE = '/app/home/user';
// Elliott sets the Live Scenario up before anything is spoken. Frontend copy,
// so it reads the same in every lesson. First person, warm, and a guide rather
// than an examiner: no attempt counts, nothing that sounds like a test.
const BRIEFING = [
  'We’re moving on to a Roleplay next. I’ll put you in a situation from your role and ask how you’d handle it.',
  'You talk it through, and I’ll come back to you a couple of times to draw out the detail. There’s nothing to prepare, and it takes about ten minutes.'
];

// Frontend default only: a lesson's own simulation.maxAttempts from the BE wins.
const MAX_SIM_ATTEMPTS = 3;

interface SimTurn {
  id: string;
  role: 'learner' | 'elliott';
  text: string;
  restored?: boolean;
}

const Crumb: React.FC<{ label: string; last?: boolean; onClick?: () => void }> = ({ label, last, onClick }) => (
  <Typography
    onClick={onClick}
    sx={{
      fontSize: 14,
      color: last ? t.textPrimary : t.textTertiary,
      fontWeight: last ? 600 : 400,
      whiteSpace: 'nowrap',
      cursor: onClick ? 'pointer' : 'default',
      '&:hover': onClick ? { color: t.textPrimary } : undefined
    }}
  >
    {label}
  </Typography>
);

// The capability the lesson builds sits beside its name, the last crumb.
const Breadcrumb: React.FC<{ crumbs: string[]; capabilityName?: string | null; onPathwayClick: () => void }> = ({
  crumbs,
  capabilityName,
  onPathwayClick
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, overflow: 'hidden' }}>
    <Crumb label={PATHWAY_LABEL} onClick={onPathwayClick} />
    {crumbs.map((crumb, i) => (
      <React.Fragment key={`${crumb}-${i}`}>
        <LuChevronRight size={18} color={t.textTertiary} style={{ flexShrink: 0 }} />
        <Crumb label={crumb} last={i === crumbs.length - 1} />
      </React.Fragment>
    ))}
    {capabilityName && crumbs.length > 0 && (
      <Box sx={{ ml: '4px', display: 'flex' }}>
        <CapabilityChip name={capabilityName} />
      </Box>
    )}
  </Box>
);

// Phone: the breadcrumb as an app bar. Back to the Dashboard (the first crumb),
// the lesson's name in full with its module under it, and the lesson tree.
const PhoneLessonHeader: React.FC<{
  crumbs: string[];
  capabilityName?: string | null;
  onBack: () => void;
  navOpen: boolean;
  onToggleNav: () => void;
}> = ({ crumbs, capabilityName, onBack, navOpen, onToggleNav }) => {
  const lessonName = crumbs[crumbs.length - 1];
  const moduleName = crumbs.length > 2 ? crumbs[crumbs.length - 2] : crumbs.length > 1 ? crumbs[0] : '';
  const iconButtonSx = {
    flexShrink: 0,
    width: 40,
    height: 40,
    display: 'grid',
    placeItems: 'center',
    borderRadius: '50%',
    color: t.textPrimary,
    cursor: 'pointer'
  } as const;
  return (
    <Box
      sx={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        minHeight: 56,
        px: '6px',
        py: '6px',
        pt: 'calc(6px + env(safe-area-inset-top))',
        borderBottom: `1px solid ${t.borderDim}`,
        position: 'relative',
        zIndex: 2
      }}
    >
      <Box role="button" aria-label={`Back to ${PATHWAY_LABEL}`} onClick={onBack} sx={iconButtonSx}>
        <LuChevronLeft size={24} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
        {lessonName && (
          <Typography sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.3, color: t.textPrimary }}>
            {lessonName}
          </Typography>
        )}
        {moduleName && (
          <Typography sx={{ fontSize: 12, lineHeight: 1.35, color: t.textTertiary, mt: '2px' }}>
            {moduleName}
          </Typography>
        )}
        {capabilityName && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: '4px' }}>
            <CapabilityChip name={capabilityName} />
          </Box>
        )}
      </Box>
      <Box
        role="button"
        aria-label={navOpen ? 'Close the lesson list' : 'Open the lesson list'}
        aria-expanded={navOpen}
        onClick={onToggleNav}
        sx={{ ...iconButtonSx, ...(navOpen && { color: t.brandLight }) }}
      >
        <LuMenu size={22} />
      </Box>
    </Box>
  );
};

// Last nav payload, kept at module scope so it survives the per-lesson remount.
// Without it the module tree blanked on every left-nav navigation while the
// next lesson loaded.
let liveNavCache: { groupId: string; programName: string; modules: NavModule[] } | null = null;

/**
 * The program the learner last opened. Redux holds it during a session, but a
 * hard refresh on a lesson URL empties the store, and the route has no program
 * id of its own — so this is what keeps a reloaded lesson on its own program.
 */
const lastSeenGroupId = (): string => {
  try {
    const raw = localStorage.getItem('lastseen');
    return raw ? String(JSON.parse(raw)?.id || '') : '';
  } catch {
    return '';
  }
};

const ElliottLessonPage: React.FC = () => {
  const navigate = useNavigate();
  // The only route into this page: /app/home/mentee/level/:levelId/task/:taskId.
  // The program id is not in the path, so it comes from the selected program.
  const { levelId, taskId } = useParams<{ levelId: string; taskId: string }>();
  const [searchParams] = useSearchParams();
  const selectedGroup = useAppSelector((state) => state.selectedGroup.data);
  // The live route carries no program id, so it comes from Redux — with
  // `lastseen` behind it, since a hard refresh empties the store but not
  // localStorage.
  const groupId: string = selectedGroup?.id || lastSeenGroupId();
  // Lesson Video Builder flag — the published lesson video renders above the content.
  const videoBuilder = useLessonVideoBuilder();
  // Improve Performance entry (?improve=1). Requested ≠ active: a green lesson
  // has no route back in (L-20), so the flag only activates when the lesson is
  // actually flagged — applyResume decides.
  const improveRequested = searchParams.get('improve') === '1';
  // Live gamification context for the Lesson Complete strip. The running score
  // is course-level, so without a program there is nothing to scope it to —
  // ask for nothing rather than for the empty string.
  const { summary: gamSummary } = useLearnerGamification(groupId || undefined);

  const [lesson, setLesson] = useState<ElliottLessonPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  // Empty without a program, so the `storageKey ? …` guards below hold: keyed
  // on an empty group the string is still truthy, and would read and clear a
  // degenerate `elliott-progress::level:task` entry shared by every program.
  const storageKey = groupId ? progressKey(groupId, String(levelId), String(taskId)) : '';

  // Lesson materials (the task's library articles) — shown at the end of the
  // lesson content, styled like the case-study cards. Uses the same task
  // articles endpoint as the classic task view; failures just hide the section.
  const [materials, setMaterials] = useState<LessonMaterial[]>([]);
  const orgLogo = useAppSelector((state) => state.orgDetails.data?.orgLogo);
  useEffect(() => {
    // Program-scoped, like the lesson read: without one there is nothing to ask for.
    if (!groupId) return undefined;
    let cancelled = false;
    setMaterials([]);
    const { orgId, location: userLoc } = getUserDetails();
    API.getRecommendedArticles({
      grpId: groupId,
      orgId,
      location: userLoc,
      userId: '',
      levelId: String(levelId),
      taskId: String(taskId)
    })
      .then((res: any) => {
        if (cancelled) return;
        const list = res?.data?.data || [];
        setMaterials(
          list.map((a: any) => ({
            id: a.id,
            title: a.articleName || 'Resource',
            thumbnail: a.coverImageUrl || '',
            readTime: a.readTime || ''
          }))
        );
      })
      .catch(() => {
        /* section simply stays hidden */
      });
    return () => {
      cancelled = true;
    };
  }, [groupId, levelId, taskId]);

  const openMaterial = (articleId: string) => {
    navigate('/app/library', { state: { navigatedArticleId: articleId } });
  };

  // Resolved lesson data. Nothing here is ever stood in for. Until the lesson arrives every piece is
  // empty, and the page renders its loading state rather than any content: a
  // learner cannot tell invented material from their own, so we never show any.
  // A missing takeaway hides the card, and a missing application intro uses a
  // neutral bridge line (or nothing without case studies).
  const lessonContent = lesson ? lesson.content : '';
  const takeaway = lesson ? lesson.takeaway || '' : '';
  const appIntroParagraphs = lesson
    ? lesson.applicationIntro?.length
      ? lesson.applicationIntro
      : (lesson.caseStudies || []).length
      ? [
          'Now let’s put this into practice. Work through the case studies below to see how the lesson plays out in real situations.'
        ]
      : []
    : [];
  const caseStudies: ElliottCaseStudy[] = lesson
    ? (lesson.caseStudies || []).map((cs, i) => ({
        ...cs,
        title: `CASE STUDY ${i + 1}: ${cs.title}`.toUpperCase()
      }))
    : [];
  const scenarioParagraphs = lesson ? (lesson.simulation ? [lesson.simulation.brief] : []) : [];
  const quiz: ElliottQuizQuestion[] = lesson ? lesson.quiz || [] : [];
  // While the next lesson loads, reuse the cached nav from the previous lesson
  // of the same program so the tree stays stable across left-nav navigation;
  // first entry renders it empty rather than guessing at a shape.
  const cachedNav = liveNavCache?.groupId === groupId ? liveNavCache : null;
  const rawNavModules: NavModule[] = lesson ? lesson.nav?.modules || [] : cachedNav?.modules || [];
  // B4-5 "New" badge: opening a lesson marks it seen (per user + program, in
  // localStorage), so the badge clears for it and stays for the others. The
  // BE mirrors the fallback as badge 'new', so that mirror clears with it;
  // a badge a publish stored (`badgeSource` 'stored': 'new', 'updated') and
  // pending actions are the BE's to clear, so the seen-set leaves them alone.
  const navModules: NavModule[] = useMemo(() => {
    // The seen-set is keyed per user + program; with no program there is no key
    // to write, and the tree is empty anyway.
    if (!groupId) return rawNavModules;
    const seen = markNewLessonSeen(getUserDetails().userId, groupId, String(taskId));
    return rawNavModules.map((m) => ({
      ...m,
      lessons: m.lessons.map((l) =>
        l.isNew && l.badgeSource !== 'stored' && seen.has(l.id)
          ? { ...l, isNew: false, badge: l.badge === 'new' ? null : l.badge }
          : l
      )
    }));
  }, [rawNavModules, groupId, taskId]);
  // Course versioning: one-time What's new pop-up for the program (org flag on).
  const navLessonTitles = useMemo(() => {
    const titles = new Map<string, string>();
    rawNavModules.forEach((m) => m.lessons.forEach((l) => titles.set(l.id, l.title)));
    return titles;
  }, [rawNavModules]);
  const whatsNew = useProgramWhatsNew(groupId || undefined, true, navLessonTitles);
  const breadcrumb = lesson
    ? [lesson.breadcrumb.programName, lesson.breadcrumb.moduleName, lesson.breadcrumb.lessonName].filter(Boolean)
    : (() => {
        // Rebuild the crumbs for the target lesson from the cached nav; empty
        // until something real names this lesson.
        const mod = cachedNav?.modules.find((m) => m.lessons.some((l) => l.id === String(taskId)));
        const les = mod?.lessons.find((l) => l.id === String(taskId));
        return [cachedNav?.programName, mod?.title, les?.title].filter(Boolean) as string[];
      })();
  const maxSimAttempts = lesson?.simulation?.maxAttempts || MAX_SIM_ATTEMPTS;
  // A lesson may have no simulation configured (edge case) — the step then auto-passes.
  const hasSimulation = lesson ? Boolean(lesson.simulation) : true;
  // A lesson may also ship no Application step (case studies toggled off or never
  // generated, and no intro) — the step then disappears from the flow entirely.
  const hasApplication = lesson ? caseStudies.length > 0 || appIntroParagraphs.length > 0 : true;
  // Quiz toggled off (BE serves an empty array) — hide its stepper node; the
  // final step still renders so the Mark-Lesson-Complete CTA stays reachable.
  const hasQuiz = lesson ? quiz.length > 0 : true;

  // Expanded/collapsed survives lesson-to-lesson navigation (the page remounts
  // per lesson) but not a new tab — session-scoped on purpose.
  const [navExpanded, setNavExpandedState] = useState(
    () => window.sessionStorage.getItem('elliottNavExpanded') === '1'
  );
  // Phone: the lesson tree is a full screen opened from the header, and it
  // starts closed on every lesson rather than following the desktop rail.
  const isMobileApp = useIsMobileApp();
  const [phoneNavOpen, setPhoneNavOpen] = useState(false);
  const setNavExpanded = (expanded: boolean) => {
    setNavExpandedState(expanded);
    try {
      window.sessionStorage.setItem('elliottNavExpanded', expanded ? '1' : '0');
    } catch {
      /* storage unavailable — nav simply reopens collapsed */
    }
  };

  // Left-nav lesson navigation: the URL is the source of truth, so selecting a
  // lesson just routes to it — the Gate remounts the page and applyResume
  // rebuilds progress (resume in-progress / review completed lessons).
  const handleSelectLesson = (lessonId: string) => {
    if (lessonId === String(taskId)) return;
    const targetModule = navModules.find((m) => m.lessons.some((l) => l.id === lessonId));
    if (!targetModule) return;
    navigate(`/app/home/mentee/level/${targetModule.id}/task/${lessonId}`);
  };

  // Next lesson in program order (for the completion celebration CTA).
  const flatLessons = navModules.flatMap((m) => m.lessons.map((l) => ({ ...l, moduleId: m.id })));
  const currentFlatIdx = flatLessons.findIndex((l) => l.id === String(taskId));
  const nextLesson = currentFlatIdx > -1 ? flatLessons.slice(currentFlatIdx + 1).find((l) => !l.locked) || null : null;

  const [draft, setDraft] = useState('');

  // Forward-only step progression: 0 Content · 1 Application · 2 Simulation · 3 Quiz.
  const [step, setStep] = useState(0);
  // Reveal gating: content/app-intro reveal their follow-on content once streamed.
  const [contentStreamed, setContentStreamed] = useState(false);
  const [appIntroStreamed, setAppIntroStreamed] = useState(false);
  const [readCaseStudies, setReadCaseStudies] = useState<Set<string>>(new Set());
  const [expandedCaseStudy, setExpandedCaseStudy] = useState<string | null>(null);

  const toggleCaseStudy = (id: string) => {
    setExpandedCaseStudy((cur) => (cur === id ? null : id));
    setReadCaseStudies((prev) => new Set(prev).add(id)); // opening marks it read (and it stays read)
  };
  const allCaseStudiesRead = caseStudies.length === 0 || caseStudies.every((cs) => readCaseStudies.has(cs.id));

  // Simulation (step 3) — free-text coaching loop, evaluated server-side against the rubric.
  const [scenarioStreamed, setScenarioStreamed] = useState(false);
  const [simTurns, setSimTurns] = useState<SimTurn[]>([]);
  const [simAttempts, setSimAttempts] = useState(0);
  const [simComplete, setSimComplete] = useState(false);
  const [simThinking, setSimThinking] = useState(false); // typing loader while Elliott evaluates

  // Quiz (step 4) — questions revealed one at a time; graded server-side per answer.
  const [quizSelected, setQuizSelected] = useState<(string | null)[]>([]);
  const [quizResults, setQuizResults] = useState<(QuizCheckResult | null)[]>([]);
  const [quizChecking, setQuizChecking] = useState<number | null>(null);
  const [quizRevealed, setQuizRevealed] = useState(1);

  // Resume markers: everything up to `restoredStep` renders instantly (no
  // re-streaming), and restored quiz questions skip their reveal animation.
  const [restoredStep, setRestoredStep] = useState(0);
  const [restoredQuizIds, setRestoredQuizIds] = useState<Set<string>>(new Set());
  // Lesson already completed server-side (taskColor green): everything is
  // reviewable, but completion is NEVER re-submitted — re-running saveAnswers
  // would recompute the member's historical MCQScore.
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);

  // ── Improve Performance (retake) state ──────────────────────────────────────
  const [improveActive, setImproveActive] = useState(false);
  // Round opened this visit (display only — the BE stamps rounds server-side).
  const [, setRetakeRound] = useState<number | null>(null);
  const [openingRetake, setOpeningRetake] = useState(false);
  // Outstanding questions being retaken this pass (one attempt each, L-16).
  const [retakeQuizIds, setRetakeQuizIds] = useState<Set<string>>(new Set());
  // Queue fetched at pass end / program completion (L-9/L-10/L-17).
  const [queueItems, setQueueItems] = useState<ImproveItem[] | null>(null);
  const [queueUnresolved, setQueueUnresolved] = useState(0);
  const [showImproveModal, setShowImproveModal] = useState(false);
  const [showAllClear, setShowAllClear] = useState(false);

  const goToImproveItem = (item: ImproveItem) => {
    const url = `/app/home/mentee/level/${item.levelId}/task/${item.taskId}?improve=1`;
    // Same lesson → the Gate's key doesn't change, so force a full reload to
    // re-derive the retake state from the server.
    if (item.taskId === String(taskId)) window.location.assign(url);
    else navigate(url);
  };

  /** Rebuild all mid-lesson state from the server payload + localStorage gates. */
  const applyResume = (data: ElliottLessonPayload) => {
    const stored = storageKey ? loadStoredProgress(storageKey) : {};
    const sim = data.simulation;

    // Completed lessons resume in full-review mode at the quiz step.
    const navLesson = (data.nav?.modules || []).flatMap((m) => m.lessons).find((l) => l.id === String(taskId));
    const completed = navLesson?.status === 'complete';
    setAlreadyCompleted(Boolean(completed));

    // Server-derived floor: the simulation transcript is authoritative.
    const simEnded = Boolean(sim && (sim.finalPassed || sim.exhausted));
    const serverStep = completed ? 3 : simEnded ? 3 : sim && sim.attemptsUsed > 0 ? 2 : 0;
    const lsStep = Math.min(Math.max(stored.step ?? 0, 0), 3);
    let resume = Math.max(serverStep, lsStep);
    // Steps this lesson doesn't ship must never be resumed AT — their blocks
    // (and advance pills) don't render, which would strand the learner. Skip
    // forward: no Application (case studies off + no intro) and no simulation
    // (never generated, or toggled off after this learner stored progress).
    const hasApp = (data.caseStudies || []).length > 0 || (data.applicationIntro || []).length > 0;
    if (!hasApp && resume === 1) resume = sim ? 2 : 3;
    if (!sim && resume === 2) resume = 3;

    // Simulation transcript → chat turns (rendered instantly). Retake-round
    // openers (Elliott's recap messages) are interleaved at their round
    // boundaries so a mid-round refresh rehydrates the full conversation.
    const turns: SimTurn[] = [];
    const introByRound = new Map(
      (sim?.retakeIntros || []).map((r) => [r.round, r.intro])
    );
    let lastTurnRound = 1;
    for (const turn of sim?.turns || []) {
      const turnRound = turn.round || 1;
      if (turnRound !== lastTurnRound && introByRound.has(turnRound)) {
        turns.push({ id: `r${turnRound}`, role: 'elliott', text: introByRound.get(turnRound) as string, restored: true });
      }
      lastTurnRound = turnRound;
      turns.push({ id: `l${turnRound}-${turn.attemptNumber}`, role: 'learner', text: turn.learnerResponse, restored: true });
      if (turn.coaching) {
        turns.push({ id: `e${turnRound}-${turn.attemptNumber}`, role: 'elliott', text: turn.coaching, restored: true });
      }
    }
    // A retake round opened but not yet attempted: its opener follows the transcript.
    const maxTurnRound = Math.max(1, ...(sim?.turns || []).map((tn) => tn.round || 1));
    for (const ri of sim?.retakeIntros || []) {
      if (ri.round > maxTurnRound) {
        turns.push({ id: `r${ri.round}`, role: 'elliott', text: ri.intro, restored: true });
      }
    }
    setSimTurns(turns);
    setSimAttempts(sim?.attemptsUsed || 0);
    setSimComplete(simEnded || completed || (!sim && resume >= 3));

    // Quiz answers restored server-first (recorded first-checks — cross-device),
    // then localStorage as a fallback for anything the server doesn't have.
    const questions = data.quiz || [];
    const serverCheckById = new Map((data.quizChecks || []).map((c) => [c.questionId, c]));
    const restoredEntry = (q: ElliottQuizQuestion): { selectedId: string; result: QuizCheckResult } | null => {
      const check = serverCheckById.get(q.id);
      if (check) {
        const selectedId = q.options.find((o) => o.label === check.answer)?.id;
        if (selectedId) {
          // Wrong + retake mode → the BE withheld the reveal fields (L-1
          // applies to reloads too); the card explains only the chosen option.
          const withheld = Boolean(data.mcqRetakeMode) && !check.correct;
          return {
            selectedId,
            result: {
              selectedCorrect: check.correct,
              correctOptionId:
                q.options.find((o) => o.label === check.correctAnswer)?.id ?? (check.correct ? selectedId : null),
              explanation: check.explanation || '',
              withheld,
              chosenReasoning: check.chosenReasoning || '',
              optionReasonings: check.optionReasonings || []
            }
          };
        }
      }
      const ls = stored.quiz?.[q.id];
      return ls ? { selectedId: ls.selectedId, result: ls.result } : null;
    };
    const entries = questions.map(restoredEntry);
    setQuizSelected(entries.map((e) => e?.selectedId ?? null));
    setQuizResults(entries.map((e) => e?.result ?? null));
    const answered = entries.filter(Boolean).length;
    setQuizRevealed(Math.max(1, Math.min(questions.length, answered + 1)));
    setRestoredQuizIds(new Set(questions.filter((q, i) => entries[i]).map((q) => q.id)));

    // Reveal gates for everything before the resume point.
    if (resume >= 1) setContentStreamed(true);
    if (resume >= 2) {
      setAppIntroStreamed(true);
      setReadCaseStudies(new Set(stored.read?.length ? stored.read : (data.caseStudies || []).map((cs) => cs.id)));
    } else if (stored.read?.length) {
      setReadCaseStudies(new Set(stored.read));
    }
    if (turns.length > 0 || resume >= 3) setScenarioStreamed(true);

    setStep(resume);
    setRestoredStep(resume);

    // ── Improve Performance entry (L-14): land at the bottom of the completed
    // conversation with only the outstanding halves reopened. A lesson that is
    // already green ignores the param entirely (L-20).
    const flagged = Boolean((navLesson as { flagged?: boolean } | undefined)?.flagged);
    if (improveRequested && flagged) {
      setImproveActive(true);

      // Quiz half: still-wrong questions restart fresh — no selection, no
      // result, shuffled options (L-16), one attempt this pass.
      const wrongIds = new Set(
        (data.quizChecks || []).filter((c) => !c.correct).map((c) => c.questionId)
      );
      if (data.mcqRetakeMode && wrongIds.size > 0) {
        setRetakeQuizIds(wrongIds);
        for (const q of questions) {
          if (!wrongIds.has(q.id)) continue;
          // Fisher-Yates on a copy — randomised relative to the previous
          // presentation; grading is by label so order is presentation-only.
          const shuffled = [...q.options];
          for (let i = shuffled.length - 1; i > 0; i -= 1) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
          }
          q.options = shuffled;
        }
        setQuizSelected(questions.map((q, i) => (wrongIds.has(q.id) ? null : (entries[i]?.selectedId ?? null))));
        setQuizResults(questions.map((q, i) => (wrongIds.has(q.id) ? null : (entries[i]?.result ?? null))));
        setRestoredQuizIds(new Set(questions.filter((q, i) => entries[i] && !wrongIds.has(q.id)).map((q) => q.id)));
        setQuizRevealed(questions.length);
      }

      // Scenario half first (L-13): an unresolved, attempted scenario reopens
      // the simulation step — an open round resumes as-is (L-18); a closed one
      // is reopened by the retake effect, which posts Elliott's round opener.
      const scenarioOutstanding = Boolean(
        sim && !sim.finalPassed && (sim.turns || []).length > 0
      );
      if (scenarioOutstanding) {
        setSimComplete(false);
        setStep(2);
        setRestoredStep(2);
      } else {
        setStep(3);
        setRestoredStep(3);
      }
    }
  };

  useEffect(() => {
    // A lesson read is scoped to its program, and the route does not name one.
    // Say so rather than guessing at one.
    if (!groupId) {
      setLoading(false);
      setLoadError('We could not tell which program this lesson belongs to. Open it from your pathway.');
      return;
    }
    let cancelled = false;
    const fetchLesson = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const res = await API.getElliottLesson({ groupId, levelId: levelId as string, taskId: taskId as string });
        if (cancelled) return;
        if (res.status === 200 && res.data) {
          // A lesson this learner only reads comes without its Roleplay and Quiz (with the backend on).
          const data = lessonForLearner(res.data as ElliottLessonPayload, audienceRequirementsBackend());
          liveNavCache = {
            groupId,
            programName: data.breadcrumb?.programName || '',
            modules: data.nav?.modules || []
          };
          setLesson(data);
          applyResume(data);
        } else {
          // Anything that is not a lesson leaves the page with nothing to show.
          // It used to fall through to the stubbed walkthrough; now it says so,
          // because a silently blank lesson has no way out of it.
          setLoadError('Failed to load this lesson. Please try again.');
        }
      } catch {
        if (!cancelled) setLoadError('Failed to load this lesson. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchLesson();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, levelId, taskId]);

  // Persist the light gates (step, opened case studies, quiz answers) so a
  // refresh resumes in place. The simulation transcript is server-persisted.
  useEffect(() => {
    if (!lesson || !storageKey) return;
    const quizStore: Record<string, StoredQuizEntry> = {};
    (lesson.quiz || []).forEach((q, i) => {
      const selectedId = quizSelected[i];
      const result = quizResults[i];
      if (selectedId && result) quizStore[q.id] = { selectedId, result };
    });
    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({ step, read: [...readCaseStudies], quiz: quizStore } as StoredProgress)
      );
    } catch {
      /* storage full/unavailable — resume simply won't survive refresh */
    }
  }, [lesson, storageKey, step, readCaseStudies, quizSelected, quizResults]);

  // ── Improve Performance: open the next scenario retake round (L-15). Runs
  // once per mount when the current round is closed; an OPEN round simply
  // resumes through the normal attempt flow (L-18, attempts carried over).
  useEffect(() => {
    if (!improveActive || !lesson?.simulation) return undefined;
    const sim = lesson.simulation;
    if (sim.finalPassed || (sim.turns || []).length === 0) return undefined;
    if (!sim.exhausted) {
      // Round still open (in-lesson abandon or an already-opened retake round).
      setRetakeRound(sim.round);
      return undefined;
    }
    let cancelled = false;
    setOpeningRetake(true);
    setSimThinking(true);
    (async () => {
      try {
        const res = await API.openElliottSimulationRetake({
          groupId,
          levelId: levelId as string,
          taskId: taskId as string
        });
        if (cancelled) return;
        const data = res.data || {};
        setRetakeRound(typeof data.round === 'number' ? data.round : null);
        setSimAttempts(0);
        if (data.intro) {
          setSimTurns((prev) => [...prev, { id: `r${data.round}`, role: 'elliott', text: data.intro }]);
        }
      } catch (err: any) {
        const refused = readOnlyRefusal(err);
        if (!cancelled && refused) dropAssessments(refused);
        else if (!cancelled) {
          toast.error(err?.response?.data?.message || 'The retake could not be opened. Try again.');
        }
      } finally {
        if (!cancelled) {
          setSimThinking(false);
          setOpeningRetake(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Depends on `lesson.simulation`, NOT on `lesson`. The queue effect below
    // rewrites `lesson` to clear this lesson's nav flag once it drops out of
    // the Improve queue — i.e. exactly when the learner has just passed. That
    // spread preserves the `simulation` reference but changes the `lesson`
    // identity, and since `simulation` is never re-fetched it still reads
    // finalPassed:false / exhausted:true. Depending on `lesson` therefore
    // re-fired this effect after a pass and opened a second, spurious retake
    // round. A genuine re-fetch replaces `simulation` wholesale, so the effect
    // still runs when it should.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [improveActive, lesson?.simulation]);

  const labelOf = (q: ElliottQuizQuestion, optionId: string): string =>
    q.options.find((o) => o.id === optionId)?.label || '';

  const applyQuizResult = (index: number, result: QuizCheckResult) => {
    setQuizResults((prev) => {
      const next = [...prev];
      next[index] = result;
      return next;
    });
    // Reveal the next question shortly after, so the explainer lands first.
    if (index + 1 < quiz.length) {
      window.setTimeout(() => setQuizRevealed((r) => Math.max(r, index + 2)), 900);
    }
  };

  const answerQuiz = async (index: number, optionId: string) => {
    const q = quiz[index];
    setQuizSelected((prev) => {
      const next = [...prev];
      next[index] = optionId;
      return next;
    });

    setQuizChecking(index);
    try {
      const res = await API.checkElliottQuizAnswer({
        groupId,
        levelId: levelId as string,
        taskId: taskId as string,
        questionId: q.id,
        answer: labelOf(q, optionId),
        // Improve Performance pass: record the next pass for this question (L-16).
        retake: improveActive && retakeQuizIds.has(q.id)
      });
      const data = res.data || {};
      // The check is idempotent per question — if the server already had a
      // recorded answer (e.g. a retried request), reconcile the selection to it.
      const recordedOptionId =
        typeof data.recordedAnswer === 'string'
          ? q.options.find((o) => o.label === data.recordedAnswer)?.id
          : undefined;
      if (recordedOptionId && recordedOptionId !== optionId) {
        setQuizSelected((prev) => {
          const next = [...prev];
          next[index] = recordedOptionId;
          return next;
        });
      }
      const effectiveSelected = recordedOptionId ?? optionId;
      const correctOptionId =
        q.options.find((o) => o.label === data.correctAnswer)?.id ?? (data.correct ? effectiveSelected : null);
      applyQuizResult(index, {
        selectedCorrect: Boolean(data.correct),
        correctOptionId,
        explanation: data.explanation || '',
        // Withheld-answer mode (L-1): a wrong answer carries no reveal fields —
        // only the chosen option's rationale.
        withheld: Boolean(data.retakeMode) && !data.correct,
        chosenReasoning: data.chosenReasoning || '',
        optionReasonings: Array.isArray(data.optionReasonings) ? data.optionReasonings : []
      });
    } catch (err) {
      const refused = readOnlyRefusal(err);
      if (refused) {
        dropAssessments(refused);
        return;
      }
      toast.error('Your answer could not be checked. Try again.');
      // Reset the selection so the learner can retry.
      setQuizSelected((prev) => {
        const next = [...prev];
        next[index] = null;
        return next;
      });
    } finally {
      setQuizChecking(null);
    }
  };

  const quizGradedCount = quizResults.filter((r) => r !== null).length;
  const allQuizAnswered = quiz.length > 0 && quizGradedCount === quiz.length;

  // Lesson Complete strip context (Cian v2) — module + lesson names from the
  // breadcrumb/nav, this lesson's quiz score as a %, and whether a Live
  // Scenario was part of the lesson. Running Score is course-level, from the
  // learner summary endpoint. When the nav can't name the module the strip
  // drops that half of its title rather than naming a made-up "Module 1".
  const stripModule =
    navModules.find((m) => m.lessons.some((l) => l.status === 'in-progress')) ??
    navModules.find((m) => m.lessons.some((l) => l.status === 'todo'));
  const stripLessonName = breadcrumb[breadcrumb.length - 1] || 'This lesson';
  const stripModuleName = stripModule?.short || null;
  const stripQuizCorrect = quizResults.filter((r) => r?.selectedCorrect).length;
  const stripLessonScorePct =
    quiz.length > 0 ? Math.round((stripQuizCorrect / quiz.length) * 100) : null;
  // Completion gate: all questions answered — or nothing to answer (lessons
  // without a quiz must still be completable), or already completed (review mode).
  const canComplete = alreadyCompleted || quiz.length === 0 || allQuizAnswered;

  // ── Improve Performance pass state for THIS lesson (L-17): both halves done
  // — pass or fail — unlocks the route straight to the next outstanding item.
  const scenarioPartDone = (() => {
    if (!improveActive) return true;
    const sim = lesson?.simulation;
    if (!sim || sim.finalPassed || (sim.turns || []).length === 0) return true;
    return simComplete;
  })();
  const quizPartDone =
    retakeQuizIds.size === 0 ||
    quiz.every((q, i) => !retakeQuizIds.has(q.id) || quizResults[i] !== null);
  const lessonPassDone = improveActive && scenarioPartDone && quizPartDone;

  // Pass finished for this lesson → fetch the queue once for flow-through
  // routing and the end-of-pass modal (L-10).
  useEffect(() => {
    if (!lessonPassDone || queueItems !== null) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await API.getElliottImproveQueue({ groupId });
        if (cancelled) return;
        const data = res.data || {};
        const items: ImproveItem[] = Array.isArray(data.items) ? data.items : [];
        setQueueItems(items);
        setQueueUnresolved(data.unresolvedCount || 0);
        // The nav flag came with the initial payload — if this lesson just
        // dropped out of the queue (resolved), clear it in place so the tree
        // doesn't contradict the outcome until the next reload.
        if (!items.some((i) => i.taskId === String(taskId))) {
          const clearFlag = (modules: NavModule[]): NavModule[] =>
            modules.map((m) => ({
              ...m,
              lessons: m.lessons.map((l) => (l.id === String(taskId) ? { ...l, flagged: false } : l))
            }));
          setLesson((prev) => (prev ? { ...prev, nav: { modules: clearFlag(prev.nav.modules) } } : prev));
          if (liveNavCache?.groupId === groupId) {
            liveNavCache = { ...liveNavCache, modules: clearFlag(liveNavCache.modules) };
          }
        }
      } catch {
        if (!cancelled) {
          setQueueItems([]);
          setQueueUnresolved(0);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonPassDone]);

  // End of the whole pass (no next item elsewhere): still-outstanding items
  // re-fire the pop-up (L-10); a clean sweep celebrates instead. The pop-up
  // only fires with a non-empty list — a count with nothing actionable (data
  // drift) must never render an empty "0 items" modal.
  useEffect(() => {
    if (!improveActive || queueItems === null) return;
    const hasNextElsewhere = queueItems.some((i) => i.taskId !== String(taskId));
    if (hasNextElsewhere) return;
    if (queueItems.length > 0) setShowImproveModal(true);
    else if (queueUnresolved === 0) setShowAllClear(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueItems]);

  // Lessons with no application intro (and no case studies) skip straight past
  // the intro gate — there is no bubble to stream.
  useEffect(() => {
    if (step >= 1 && appIntroParagraphs.length === 0) setAppIntroStreamed(true);
  }, [step, appIntroParagraphs.length]);

  // Belt-and-braces: the flow must NEVER strand on a step the lesson doesn't
  // ship — those blocks (and their advance pills) don't render. Any path that
  // lands on one skips forward.
  useEffect(() => {
    if (step === 1 && !hasApplication) {
      if (hasSimulation) {
        setStep(2);
      } else {
        setSimComplete(true);
        setStep(3);
      }
    }
  }, [step, hasApplication, hasSimulation]);
  useEffect(() => {
    if (step === 2 && !hasSimulation) {
      setSimComplete(true);
      setStep(3);
    }
  }, [step, hasSimulation]);

  // Completion — persists through the standard saveAnswers flow (drives goalProgress,
  // milestones, MCQScore and completion notifications), then celebrates.
  const [showCelebration, setShowCelebration] = useState(false);
  const [completing, setCompleting] = useState(false);
  const markLessonComplete = async () => {
    if (completing) return;
    if (alreadyCompleted) {
      // Review mode — completion is already on record; never re-submit
      // (saveAnswers would recompute the historical MCQScore).
      navigate(PATHWAY_ROUTE);
      return;
    }
    setCompleting(true);
    try {
      const MCQanswers = quiz
        .map((q, i) => ({
          MCQquesId: q.id,
          MCQAns: quizSelected[i] ? labelOf(q, quizSelected[i] as string) : '',
          // saveAnswerSchema requires the key; the server re-grades and overwrites it
          result: quizResults[i]?.selectedCorrect ?? false
        }))
        .filter((a) => a.MCQAns);
      const saveRes = await API.saveAnswers({
        groupId,
        levelId: levelId as string,
        taskId: taskId as string,
        menteeAsmtAnswer: 'yes',
        answers: [],
        MCQanswers,
        cancelToken: undefined
      });

      // Analytics — Lesson Completed (+ All Lessons Completed at 100%), with the
      // scores from this session: quiz % (graded inline) and the fresh progress.
      const goalProgress = saveRes?.data?.goalProgress;
      const gradedCount = quizResults.filter(Boolean).length;
      const correctCount = quizResults.filter((r) => r?.selectedCorrect).length;
      const sessionMcqScore = gradedCount > 0 ? Math.round((correctCount / gradedCount) * 100) : undefined;
      const lessonProps: Record<string, unknown> = {
        [attributeKeys.programName]: lesson?.breadcrumb.programName || '',
        lessonName: lesson?.breadcrumb.lessonName || '',
        moduleName: lesson?.breadcrumb.moduleName || '',
        levelId,
        taskId
      };
      if (typeof goalProgress === 'number') {
        lessonProps[attributeKeys.progressScore] = goalProgress;
        lessonProps[attributeKeys.userProgramStatus] = goalProgress >= 100 ? 'Completed' : 'In Progress';
      }
      if (sessionMcqScore !== undefined) lessonProps[attributeKeys.mcqScore] = sessionMcqScore;
      trackWithBaseProps(events.lessonCompleted, lessonProps);
      if (typeof goalProgress === 'number' && goalProgress >= 100) {
        trackWithBaseProps(events.allLessonsCompleted, {
          ...lessonProps,
          [attributeKeys.userProgramStatus]: 'Completed'
        });
      }

      // Completion is now server-persisted — the local resume snapshot is obsolete.
      if (storageKey) {
        try {
          window.localStorage.removeItem(storageKey);
        } catch {
          /* noop */
        }
      }
      setStep(4); // beyond Quiz (index 3) so every step reads as complete in the stepper

      // L-9: finishing the program's FINAL lesson with outstanding items swaps
      // the plain celebration for the Improve Performance pop-up — every
      // outstanding item listed, dismissible, dismissing clears nothing.
      if (typeof goalProgress === 'number' && goalProgress >= 100) {
        try {
          const qres = await API.getElliottImproveQueue({ groupId });
          const qdata = qres.data || {};
          const qItems: ImproveItem[] = Array.isArray(qdata.items) ? qdata.items : [];
          // Only a non-empty, actionable list swaps the celebration (L-9).
          if (qItems.length > 0) {
            setQueueItems(qItems);
            setQueueUnresolved(qdata.unresolvedCount || 0);
            setShowImproveModal(true);
            return;
          }
        } catch {
          /* queue unavailable — fall through to the normal celebration */
        }
      }
      setShowCelebration(true);
    } catch {
      toast.error('The lesson could not be marked complete. Try again.');
    } finally {
      setCompleting(false);
    }
  };

  // ── Voice conversation (Live Scenario only — every other step stays text) ──
  // Turn-based, half-duplex: Elliott speaks a full turn, then the mic opens;
  // the finalized transcript goes through the SAME submitSimResponse flow, so
  // the rubric eval / coaching loop / attempt cap are untouched by voice.
  const orgData = useAppSelector((state) => state.orgDetails.data);
  const voiceSupported = isVoiceConversationSupported();
  const voiceFlagOn = isElliottVoiceEnabled(orgData?._id, orgData?.authId, getUserDetails().orgId);
  const voiceAvailable = voiceFlagOn && voiceSupported;
  const [voiceMode, setVoiceMode] = useState(false);
  // 'denied' → mic permission refused: silently fall back to text + a subtle
  // note with a retry affordance (never a blocking error).
  const [voiceFallback, setVoiceFallback] = useState<null | 'denied'>(null);
  const voiceModeRef = useRef(false);
  voiceModeRef.current = voiceMode;
  const lastSpokenRef = useRef('');
  const lastSpokenTurnIdRef = useRef<string | null>(null);
  // Bubble whose typewriter is HELD until Elliott's audio actually starts —
  // keeps the caption in step with the voice instead of racing ahead of it.
  const [pendingSpeechId, setPendingSpeechId] = useState<string | null>(null);
  // The message Elliott is voicing right now: its typewriter freezes while she
  // is paused, and the sentence she is on is highlighted in it.
  const [speakingTurnId, setSpeakingTurnId] = useState<string | null>(null);

  // The answer field is shared by speaking and typing: spoken phrases are
  // appended as they are written down, and Send answer sends whatever it holds.
  // Read through a ref because phrases and Send can land before a re-render.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const setDraftNow = (next: string) => {
    draftRef.current = next;
    setDraft(next);
  };

  const voice = useVoiceConversation({
    active: voiceMode,
    onTranscript: (text) => {
      const kept = draftRef.current.trimEnd();
      setDraftNow(kept ? `${kept} ${text}` : text);
    },
    onReadyToSend: () => {
      const text = draftRef.current.trim();
      if (!text || step !== 2 || simComplete || simThinking) return false;
      setDraftNow('');
      submitSimResponse(text);
      return true;
    },
    onMicDenied: () => {
      setVoiceMode(false);
      setVoiceFallback('denied');
    },
    // Elliott's last turn (or the scenario) biases the server transcription.
    getSttContext: () => {
      const lastElliott = [...simTurns].reverse().find((tn) => tn.role === 'elliott')?.text;
      return (lastElliott || scenarioParagraphs.join(' ')).slice(0, 500);
    }
  });

  const speakElliott = (
    text: string,
    opts?: {
      turnId?: string;
      listenAfter?: boolean;
      onSettled?: () => void;
      // A brand-new message whose typewriter should wait for the audio. Never
      // for a replay: that text is already on screen, and holding it would
      // restart its typewriter from nothing.
      holdCaption?: boolean;
    }
  ) => {
    if (!voiceModeRef.current || !text.trim()) return;
    lastSpokenRef.current = text;
    if (opts?.turnId) lastSpokenTurnIdRef.current = opts.turnId;
    setSpeakingTurnId(opts?.turnId ?? null);
    if (opts?.turnId && opts.holdCaption) setPendingSpeechId(opts.turnId);
    voice.speak(text, {
      onStart: () => setPendingSpeechId(null),
      onSpoken: () => {
        setPendingSpeechId(null);
        setSpeakingTurnId(null);
        opts?.onSettled?.();
        if (!voiceModeRef.current) return;
        if (opts?.listenAfter) voice.listen();
        // Conversation over (pass / attempts exhausted): hand back to the text
        // UI — the advance pill is already on screen.
        else setVoiceMode(false);
      }
    });
  };

  // Replay: Elliott reads her last message again from the start. Anything the
  // learner had already said still lands in the field, and their turn resumes
  // when she finishes. During a replay the same button skips it.
  const [repeating, setRepeating] = useState(false);
  const handleRepeat = () => {
    if (!lastSpokenRef.current.trim()) return;
    setRepeating(true);
    speakElliott(lastSpokenRef.current, {
      turnId: lastSpokenTurnIdRef.current ?? undefined,
      listenAfter: !simComplete,
      onSettled: () => setRepeating(false)
    });
  };
  const handleSkipRepeat = () => {
    voice.skipSpeaking();
    setRepeating(false);
  };

  // Leaving voice mode must never leave a caption held back (or a stuck Skip).
  useEffect(() => {
    if (!voiceMode) {
      setPendingSpeechId(null);
      setSpeakingTurnId(null);
      setRepeating(false);
    }
  }, [voiceMode]);

  // Nothing is spoken and no mic opens until the learner asks. A fresh
  // scenario opens with Elliott's briefing and a Start button; one already
  // under way (turns restored, or finished) is simply open, and its mic still
  // waits for the learner to choose to speak.
  const [scenarioStarted, setScenarioStarted] = useState(false);
  const [briefingStreamed, setBriefingStreamed] = useState(false);
  const scenarioOpen = scenarioStarted || simTurns.length > 0 || simComplete;
  const startScenario = () => {
    setScenarioStarted(true);
    if (!voiceAvailable) return;
    // Hold the scenario's typewriter from its first frame until the audio starts.
    setPendingSpeechId('scenario');
    setVoiceMode(true);
  };

  // Entering voice mode: a fresh sim gets the scenario spoken aloud; a resumed
  // one goes straight to listening EXCEPT when Elliott holds the floor with a
  // turn she has never voiced — the Improve Performance retake, which opens
  // with a brand-new intro. Jumping to the mic there left the learner listening
  // to silence with no idea what was being asked. Waits out openingRetake/
  // simThinking so the mic never opens mid-eval.
  const voiceEntryDoneRef = useRef(false);
  useEffect(() => {
    if (!voiceMode) {
      voiceEntryDoneRef.current = false;
      return;
    }
    if (voiceEntryDoneRef.current || simThinking || openingRetake) return;
    voiceEntryDoneRef.current = true;
    if (simComplete) return;
    if (simTurns.length === 0) {
      speakElliott(scenarioParagraphs.join('\n\n'), { turnId: 'scenario', listenAfter: true, holdCaption: true });
      return;
    }
    // `restored` marks turns replayed from the server (already seen); anything
    // else is new, so a trailing un-restored Elliott turn still needs voicing.
    // Its text may already be typed out, so the caption is not held back.
    const lastTurn = simTurns[simTurns.length - 1];
    if (lastTurn && lastTurn.role === 'elliott' && !lastTurn.restored && lastTurn.text.trim()) {
      speakElliott(lastTurn.text, { turnId: lastTurn.id, listenAfter: true });
    } else {
      // Nothing new to hear, so the mic opens. Replay still has something to
      // replay: Elliott's last message, or the scenario itself.
      const lastElliott = [...simTurns].reverse().find((tn) => tn.role === 'elliott');
      lastSpokenRef.current = lastElliott?.text || scenarioParagraphs.join('\n\n');
      lastSpokenTurnIdRef.current = lastElliott?.id || 'scenario';
      voice.listen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceMode, simThinking, openingRetake]);

  // Leaving the sim step for any reason shuts the voice session down.
  useEffect(() => {
    if (step !== 2 && voiceMode) setVoiceMode(false);
  }, [step, voiceMode]);

  const switchToVoice = () => {
    setVoiceFallback(null);
    setVoiceMode(true);
  };

  // Elliott paused mid-message: her typewriter holds where it is too.
  const elliottHeld = voice.state === 'paused' && voice.pausedDuring === 'speaking';

  const pushElliottTurn = (attempt: number, text: string) => {
    setSimTurns((prev) => [...prev, { id: `e${attempt}`, role: 'elliott', text }]);
  };

  // Audience Requirements: a Roleplay or Quiz attempt the backend refused because this learner only reads
  // the course (409 { readOnly: true }). Not an error of theirs: the lesson drops its Roleplay and Quiz as
  // if it never had them, and Mark Complete is next. The stepper's effects move them past the gone steps.
  const dropAssessments = (refused: { message: string }) => {
    setLesson((prev) => (prev ? withoutAssessments(prev) : prev));
    setSimTurns([]);
    setSimComplete(true);
    setImproveActive(false);
    setRetakeQuizIds(new Set());
    setQuizSelected([]);
    setQuizResults([]);
    if (voiceModeRef.current) setVoiceMode(false);
    toast.info(refused.message || 'This lesson is yours to read: its Roleplay and Quiz are for others.');
  };

  const submitSimResponse = async (text: string) => {
    const attempt = simAttempts + 1;
    setSimAttempts(attempt);
    setSimTurns((prev) => [...prev, { id: `l${attempt}`, role: 'learner', text }]);
    setSimThinking(true);

    try {
      const res = await API.evaluateElliottSimulation({
        groupId,
        levelId: levelId as string,
        taskId: taskId as string,
        response: text
      });
      const data = res.data || {};
      pushElliottTurn(attempt, data.coaching || '');
      if (typeof data.attemptNumber === 'number') setSimAttempts(data.attemptNumber);
      if (data.passed || data.exhausted) setSimComplete(true);
      speakElliott(data.coaching || '', {
        turnId: `e${attempt}`,
        listenAfter: !(data.passed || data.exhausted),
        holdCaption: true
      });
    } catch (err: any) {
      // Attempt didn't register server-side — roll back the local count so retry works.
      setSimAttempts((n) => Math.max(0, n - 1));
      const refused = readOnlyRefusal(err);
      if (refused) {
        dropAssessments(refused);
        return;
      }
      const msg: string = err?.response?.data?.message || '';
      // BE state codes are for the client, never for the learner's eyes.
      if (msg.startsWith('ROUND_EXHAUSTED')) {
        toast.info('This round is closed. Open Retakes to try the Roleplay again.');
        // The round can't take responses — voice has nothing to listen for.
        if (voiceModeRef.current) setVoiceMode(false);
      } else if (msg.startsWith('OPEN_ROUND')) {
        toast.info('This round is still open. Send your next response.');
        if (voiceModeRef.current) voice.listen();
      } else {
        toast.error(msg || 'Elliott could not evaluate your response. Try again.');
        // Recoverable failure — reopen the mic so the learner just speaks again.
        if (voiceModeRef.current) voice.listen();
      }
    } finally {
      setSimThinking(false);
    }
  };

  const handleSend = () => {
    const text = draft.trim();
    if (!text) return;
    if (step === 2 && !simComplete && !simThinking) submitSimResponse(text);
    setDraft('');
  };

  // Course versioning: New / Updated on the parts of this lesson a publish changed.
  const stepBadges = new Map<LessonStepKey, 'new' | 'updated'>();
  Object.entries(lesson?.partBadges || {}).forEach(([part, badge]) => {
    if (PART_STEP[part] && badge) stepBadges.set(PART_STEP[part], badge);
  });

  // Logical step indices stay 0..3 everywhere; the stepper simply hides the
  // nodes this lesson doesn't ship (Application / Simulation missing or toggled off).
  const steps: Step[] = DEFAULT_STEPS.map((s, i) => ({
    ...s,
    status: (i < step ? 'complete' : i === step ? 'active' : 'upcoming') as Step['status'],
    badge: stepBadges.get(s.key)
  })).filter(
    (s) =>
      (hasSimulation || s.key !== 'simulation') &&
      (hasApplication || s.key !== 'application') &&
      (hasQuiz || s.key !== 'quiz')
  );

  // Keep the newest content / active gate in view as the thread grows.
  const threadEndRef = useRef<HTMLDivElement>(null);
  // Defer a tick so freshly-revealed content (options, explainer, next question) has painted first.
  const scrollToEnd = () =>
    window.setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 60);
  useEffect(() => {
    scrollToEnd();
  }, [
    step,
    contentStreamed,
    appIntroStreamed,
    allCaseStudiesRead,
    scenarioStreamed,
    simTurns.length,
    simComplete,
    simThinking,
    quizRevealed,
    quizGradedCount
  ]);

  const fadeInSx = {
    animation: 'elliottFadeIn 0.4s ease',
    '@keyframes elliottFadeIn': { from: { opacity: 0, transform: 'translateY(4px)' }, to: { opacity: 1 } }
  } as const;

  // Advancing from the Application step: skip the simulation entirely when the
  // lesson has no rubric configured (nothing to evaluate against).
  const advanceFromApplication = () => {
    if (hasSimulation) {
      setStep(2);
    } else {
      setSimComplete(true);
      setStep(3);
    }
  };

  // Advancing from the Content step: skip Application when the lesson ships no
  // case studies and no intro (e.g. case studies toggled off by an admin).
  const advanceFromContent = () => {
    if (hasApplication) setStep(1);
    else advanceFromApplication();
  };

  // Improve Performance flow-through: the next outstanding item, or back to the Dashboard.
  const improveNextPill = (() => {
    const nextItem = (queueItems || []).find((i) => i.taskId !== String(taskId)) || null;
    return nextItem ? (
      <QuickReplyPill
        label={`Next up: ${nextItem.lessonTitle}`}
        icon="arrow"
        onClick={() => goToImproveItem(nextItem)}
      />
    ) : (
      <QuickReplyPill label="Back to Dashboard" onClick={() => navigate(PATHWAY_ROUTE)} />
    );
  })();
  const markCompleteButton = (
    <Box
      onClick={markLessonComplete}
      aria-busy={completing || undefined}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: '24px',
        py: '10px',
        borderRadius: '8px',
        cursor: completing ? 'progress' : 'pointer',
        color: t.textPrimary,
        fontWeight: 600,
        fontSize: 15,
        background: t.brandyTextGradient,
        transition: 'filter 0.15s ease',
        '&:hover': { filter: completing ? 'none' : 'brightness(1.08)' },
        // Working: the house disabled look, with the label saying what's happening.
        ...(completing && {
          background: 'rgba(0, 0, 0, 0.2)',
          boxShadow: 'inset 0 0 0 1px #2A2A2A',
          color: '#565656'
        })
      }}
    >
      {completing ? 'Completing...' : alreadyCompleted ? 'Lesson completed. Back to Dashboard' : 'Mark Complete'}
    </Box>
  );
  // Phone: the reply box only while the Roleplay takes a reply (or is spoken);
  // otherwise the bar holds the step's one forward action, so the learner never
  // scrolls to the end of the thread to find it.
  // Once the Roleplay is complete the bar hands over to the advance action, voice or not.
  const phoneReply = step === 2 && !simComplete && (voiceMode || (hasSimulation && scenarioOpen));
  const phoneAction: React.ReactNode = (() => {
    if (!isMobileApp || loading || loadError) return null;
    if (step >= 3) {
      if (improveActive && lessonPassDone && queueItems !== null) return improveNextPill;
      return canComplete && !improveActive ? markCompleteButton : null;
    }
    if (step === 2 && hasSimulation) {
      if (simComplete && !openingRetake) {
        return (
          <QuickReplyPill
            label={
              improveActive
                ? retakeQuizIds.size > 0
                  ? 'On to the quiz retake'
                  : 'Continue'
                : hasQuiz
                ? 'Time for the quiz'
                : 'Wrap up the lesson'
            }
            icon="arrow"
            onClick={() => setStep(3)}
          />
        );
      }
      return !scenarioOpen && briefingStreamed ? (
        <QuickReplyPill label="Start Roleplay" icon="arrow" onClick={startScenario} />
      ) : null;
    }
    if (step === 1 && hasApplication) {
      return appIntroStreamed && allCaseStudiesRead ? (
        <QuickReplyPill label="I've got the gist" onClick={advanceFromApplication} />
      ) : null;
    }
    return step === 0 && contentStreamed ? (
      <QuickReplyPill label="I've got the gist" onClick={advanceFromContent} />
    ) : null;
  })();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
        bgcolor: t.bg,
        position: 'relative',
        overflow: 'hidden',
        // Phone: the visible height while Safari's toolbar shows and hides.
        [MOBILE_SX]: { height: '100dvh' }
      }}
    >
      {/* Full-width top header: breadcrumb (left) + Dosen logo (right) */}
      {isMobileApp ? (
        <PhoneLessonHeader
          crumbs={breadcrumb}
          capabilityName={lesson?.capabilityName ?? null}
          onBack={() => navigate(PATHWAY_ROUTE)}
          navOpen={phoneNavOpen}
          onToggleNav={() => setPhoneNavOpen((open) => !open)}
        />
      ) : (
        <Box
          sx={{
            height: 86,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: '32px',
            borderBottom: `1px solid ${t.borderDim}`,
            position: 'relative',
            zIndex: 2
          }}
        >
          <Breadcrumb
            crumbs={breadcrumb}
            capabilityName={lesson?.capabilityName ?? null}
            onPathwayClick={() => navigate(PATHWAY_ROUTE)}
          />
          <Box
            component="img"
            src={dosenLogo}
            alt="Dosen"
            sx={{ height: 36, width: 92, objectFit: 'contain', flexShrink: 0 }}
          />
        </Box>
      )}

      {/* Body: left nav + lesson content, beneath the header */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', position: 'relative', zIndex: 1 }}>
        <LeftNav
          modules={navModules}
          expanded={isMobileApp ? phoneNavOpen : navExpanded}
          activeLessonId={String(taskId)}
          onToggle={isMobileApp ? setPhoneNavOpen : setNavExpanded}
          onSelectLesson={handleSelectLesson}
          title={isMobileApp ? breadcrumb[0] : undefined}
        />

        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {loading ? (
            <Box
              aria-busy="true"
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                py: '25px',
                px: '130px',
                [MOBILE_SX]: { py: '16px', px: '16px' }
              }}
            >
              <Bone height={24} width="60%" sx={{ mx: 'auto' }} />
              <Box
                sx={{
                  maxWidth: 1146,
                  width: '100%',
                  mx: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px',
                  pt: '20px'
                }}
              >
                <Bone height={18} width="85%" />
                <Bone height={18} width="92%" />
                <Bone height={18} width="70%" />
                <Bone height={120} radius="12px" />
                <Bone height={18} width="60%" />
              </Box>
            </Box>
          ) : loadError ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px'
              }}
            >
              <Typography sx={{ fontSize: 15, color: t.textSecondary }}>{loadError}</Typography>
              <QuickReplyPill label="Back to Dashboard" onClick={() => navigate(PATHWAY_ROUTE)} />
            </Box>
          ) : (
            <>
              {/* Stepper */}
              <Box sx={{ flexShrink: 0, py: '25px', px: '130px', [MOBILE_SX]: { py: '12px', px: '8px' } }}>
                <StepStepper steps={steps} />
              </Box>

              {/* Chat thread — one continuous, forward-only conversation */}
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  px: '130px',
                  [MOBILE_SX]: { px: '16px', overscrollBehaviorY: 'contain' }
                }}
              >
                <Box
                  sx={{
                    maxWidth: 1146,
                    mx: 'auto',
                    pt: '20px',
                    pb: '40px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                    [MOBILE_SX]: { pt: '8px', pb: '24px', gap: '16px' }
                  }}
                >
                  {/* Lesson video (AI Lesson Video Builder) — only when one is published */}
                  {videoBuilder && (
                    <LearnerLessonVideo
                      programId={groupId}
                      moduleId={String(levelId)}
                      lessonId={String(taskId)}
                      wrap={(node) => (
                        <ElliottMessage stacked>
                          <Box sx={messageBodySx}>Here's a quick take on this lesson. Watch this, then we'll put it into practice.</Box>
                          <Box sx={{ mt: 1.5 }}>{node}</Box>
                        </ElliottMessage>
                      )}
                    />
                  )}

                  {/* Step 1 — Lesson Content (instant when resuming past it) */}
                  <ElliottMessage stacked>
                    <StreamingLessonContent
                      content={lessonContent}
                      sx={messageBodySx}
                      enabled={restoredStep < 1}
                      onDone={() => setContentStreamed(true)}
                    />
                  </ElliottMessage>

                  {contentStreamed && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px', ...fadeInSx }}>
                      {/* TAKEAWAY + quick-reply align with Elliott's text column (left 60, right 68). */}
                      {takeaway && (
                        <Box sx={{ pl: '60px', pr: '68px', [MOBILE_SX]: { pl: 0, pr: 0 } }}>
                          <TakeawayCard>{takeaway}</TakeawayCard>
                        </Box>
                      )}
                      {/* Lesson materials — attached articles, shown case-study-style at the
                          end of the lesson content. */}
                      {materials.length > 0 && (
                        <Box
                          sx={{
                            pl: '60px',
                            pr: '68px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                            [MOBILE_SX]: { pl: 0, pr: 0 }
                          }}
                        >
                          <Typography
                            sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.5px', color: t.textSecondary }}
                          >
                            RESOURCES
                          </Typography>
                          {materials.map((mat) => (
                            <LessonMaterialCard
                              key={mat.id}
                              material={mat}
                              fallbackThumbnail={orgLogo || dosenFavicon}
                              onOpenInLibrary={() => openMaterial(mat.id)}
                            />
                          ))}
                        </Box>
                      )}
                      {step === 0 && !isMobileApp && (
                        <Box sx={{ pl: '60px' }}>
                          <QuickReplyPill label="I've got the gist" onClick={advanceFromContent} />
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Step 2 — Application (case studies) */}
                  {step >= 1 && hasApplication && (
                    <>
                      {appIntroParagraphs.length > 0 && (
                        <ElliottMessage stacked>
                          <StreamingText
                            paragraphs={appIntroParagraphs}
                            sx={messageBodySx}
                            enabled={restoredStep < 2}
                            onDone={() => setAppIntroStreamed(true)}
                          />
                        </ElliottMessage>
                      )}

                      {appIntroStreamed && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px', ...fadeInSx }}>
                          <Box
                            sx={{
                              pl: '60px',
                              pr: '68px',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '12px',
                              [MOBILE_SX]: { pl: 0, pr: 0 }
                            }}
                          >
                            {caseStudies.map((cs) => (
                              <CaseStudyCard
                                key={cs.id}
                                title={cs.title}
                                sections={cs.sections}
                                read={readCaseStudies.has(cs.id)}
                                expanded={expandedCaseStudy === cs.id}
                                onToggle={() => toggleCaseStudy(cs.id)}
                              />
                            ))}
                          </Box>
                          {/* Advance pill appears only once every case study has been opened. */}
                          {step === 1 && allCaseStudiesRead && !isMobileApp && (
                            <Box sx={{ pl: '60px', ...fadeInSx }}>
                              <QuickReplyPill label="I've got the gist" onClick={advanceFromApplication} />
                            </Box>
                          )}
                        </Box>
                      )}
                    </>
                  )}

                  {/* Step 3 — Live Scenario (response loop, server-side rubric eval) */}
                  {step >= 2 && hasSimulation && (
                    <>
                      {/* Elliott sets it up first, and nothing is spoken until Start.
                          Stays in the thread once started; resumed sessions skip it. */}
                      {(!scenarioOpen || scenarioStarted) && (
                        <ElliottMessage stacked tag={{ label: 'Roleplay', dot: true }}>
                          <StreamingText
                            paragraphs={BRIEFING}
                            sx={messageBodySx}
                            onDone={() => {
                              setBriefingStreamed(true);
                              scrollToEnd();
                            }}
                          />
                        </ElliottMessage>
                      )}
                      {!scenarioOpen && briefingStreamed && !isMobileApp && (
                        <Box sx={{ pl: '60px', display: 'flex', alignItems: 'center', gap: '10px', ...fadeInSx }}>
                          <QuickReplyPill label="Start Roleplay" icon="arrow" onClick={startScenario} />
                          {/* Detail lives here rather than on the page. */}
                          {voiceAvailable && (
                            <Tooltip title="I’ll speak my side out loud. You can pause me, or replay anything I’ve said, at any point.">
                              <Box
                                tabIndex={0}
                                aria-label="How the Roleplay works"
                                sx={{
                                  display: 'grid',
                                  placeItems: 'center',
                                  width: 30,
                                  height: 30,
                                  borderRadius: '50%',
                                  color: t.textTertiary,
                                  cursor: 'default',
                                  '&:hover, &:focus-visible': { color: t.textSecondary }
                                }}
                              >
                                <LuInfo size={17} />
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                      )}

                      {scenarioOpen && (
                        <>
                          <ElliottMessage stacked tag={{ label: 'Roleplay', dot: true }}>
                            <StreamingText
                              paragraphs={scenarioParagraphs}
                              sx={messageBodySx}
                              // Voice mode: slow the typewriter to roughly speech
                              // pace so the bubble doubles as a live caption, and
                              // hold it until Elliott's audio actually starts.
                              charsPerTick={voiceMode ? 1 : 3}
                              tickMs={voiceMode ? 45 : 16}
                              paused={voiceMode && pendingSpeechId === 'scenario'}
                              frozen={elliottHeld && speakingTurnId === 'scenario'}
                              highlight={speakingTurnId === 'scenario' ? voice.caption?.words.join(' ') : undefined}
                              enabled={!(simTurns.some((tn) => tn.restored) || restoredStep >= 3)}
                              onDone={() => setScenarioStreamed(true)}
                            />
                          </ElliottMessage>

                          {/* Rehydrated sessions: the loop already ended in a previous visit. */}
                          {scenarioStreamed && simComplete && simTurns.length === 0 && (
                            <ElliottMessage stacked tag={{ label: 'Roleplay', dot: true }}>
                              <StreamingText
                                paragraphs={[
                                  'You’ve already completed this Roleplay. Nice work, let’s keep going.'
                                ]}
                                sx={messageBodySx}
                                onDone={scrollToEnd}
                              />
                            </ElliottMessage>
                          )}

                          {simTurns.map((turn) =>
                            turn.role === 'learner' ? (
                              <LearnerMessage key={turn.id}>{turn.text}</LearnerMessage>
                            ) : (
                              <ElliottMessage stacked key={turn.id} tag={{ label: 'Roleplay', dot: true }}>
                                <StreamingText
                                  paragraphs={[turn.text]}
                                  sx={messageBodySx}
                                  charsPerTick={voiceMode ? 1 : 3}
                                  tickMs={voiceMode ? 45 : 16}
                                  paused={voiceMode && pendingSpeechId === turn.id}
                                  frozen={elliottHeld && speakingTurnId === turn.id}
                                  highlight={speakingTurnId === turn.id ? voice.caption?.words.join(' ') : undefined}
                                  enabled={!turn.restored}
                                  onDone={turn.restored ? undefined : scrollToEnd}
                                />
                              </ElliottMessage>
                            )
                          )}

                          {/* Elliott evaluating the response */}
                          {simThinking && (
                            <ElliottMessage stacked tag={{ label: 'Roleplay', dot: true }}>
                              <TypingDots />
                            </ElliottMessage>
                          )}

                          {/* Pass or attempt-cap exhaust → advance prompt (looks the same to the learner). */}
                          {step === 2 && simComplete && !openingRetake && !isMobileApp && (
                            <Box sx={{ pl: '60px', ...fadeInSx }}>
                              <QuickReplyPill
                                label={
                                  improveActive
                                    ? retakeQuizIds.size > 0
                                      ? 'On to the quiz retake'
                                      : 'Continue'
                                    : hasQuiz
                                    ? 'Time for the quiz'
                                    : 'Wrap up the lesson'
                                }
                                icon="arrow"
                                onClick={() => setStep(3)}
                              />
                            </Box>
                          )}
                        </>
                      )}
                    </>
                  )}

                  {/* Step 4 — Quiz (MCQ one at a time + inline explainer, graded server-side) */}
                  {step >= 3 && (
                    <>
                      {quiz.slice(0, quizRevealed).map((q, i) => (
                        <ElliottMessage stacked key={q.id}>
                          <QuizCard
                            question={q.question}
                            options={q.options}
                            selectedId={quizSelected[i] ?? null}
                            result={quizResults[i] ?? null}
                            checking={quizChecking === i}
                            instant={restoredQuizIds.has(q.id)}
                            onSelect={(id) => answerQuiz(i, id)}
                            onReveal={scrollToEnd}
                          />
                        </ElliottMessage>
                      ))}

                      {/* Improve Performance flow-through (L-17): the moment this
                          lesson's retake items are done — pass or fail — route
                          straight to the next outstanding item, no dashboard hop. */}
                      {improveActive && lessonPassDone && queueItems !== null && !isMobileApp && (
                        <Box sx={{ pl: '60px', ...fadeInSx }}>{improveNextPill}</Box>
                      )}

                      {/* Completion unlocks once every question is answered (or there is
                          nothing to answer). Already-completed lessons are review-only —
                          the CTA just navigates back, never re-submits. Hidden during an
                          Improve Performance pass — flow-through replaces it. */}
                      {canComplete && !improveActive && !isMobileApp && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', ...fadeInSx }}>{markCompleteButton}</Box>
                      )}
                    </>
                  )}

                  <Box ref={threadEndRef} />
                </Box>
              </Box>

              {/* Sticky input bar — swapped for the voice controls while the
                  spoken simulation is active. The voice controls handle typing
                  themselves; the text bar is for when voice is not in use. */}
              {(!isMobileApp || phoneReply || phoneAction) && (
                <Box
                  sx={{
                    flexShrink: 0,
                    px: '130px',
                    py: '20px',
                    // Phone: pinned above the home indicator, the reply box or the step's next action.
                    [MOBILE_SX]: {
                      px: '12px',
                      pt: '10px',
                      pb: 'max(10px, env(safe-area-inset-bottom))',
                      borderTop: `1px solid ${t.borderDim}`,
                      bgcolor: t.bg
                    }
                  }}
                >
                  {voiceFallback === 'denied' && step === 2 && !simComplete && (
                    <Box sx={{ width: '100%', maxWidth: 1146, mx: 'auto', mb: '8px', textAlign: 'right' }}>
                      <Typography sx={{ fontSize: 13, color: t.textTertiary }}>
                        Voice isn’t available here, so I’ve switched to text.
                      </Typography>
                    </Box>
                  )}
                  {/* Keyed so every swap between voice and text, including the hand
                    back when the scenario ends, fades in rather than snapping. */}
                  {isMobileApp && !phoneReply ? (
                    <Box
                      key="action"
                      sx={{
                        display: 'flex',
                        ...fadeInSx,
                        '& > *': { flex: 1, minHeight: 46, boxSizing: 'border-box' },
                        '& .MuiTypography-root': { whiteSpace: 'normal', textAlign: 'center' }
                      }}
                    >
                      {phoneAction}
                    </Box>
                  ) : (
                    <Box key={voiceMode && step === 2 ? 'voice' : 'text'} sx={fadeInSx}>
                      {voiceMode && step === 2 ? (
                        <VoiceBar
                          state={voice.state}
                          pausedDuring={voice.pausedDuring}
                          inputMode={voice.inputMode}
                          partial={voice.partial}
                          heardSpeech={voice.heardSpeech}
                          writing={voice.hearing || voice.transcribing}
                          repeating={repeating}
                          value={draft}
                          onChange={setDraftNow}
                          onSend={voice.send}
                          onToggleMic={voice.toggleMic}
                          onInputMode={voice.setInputMode}
                          onReplay={handleRepeat}
                          onSkip={handleSkipRepeat}
                          onPause={voice.pause}
                          onResume={voice.resume}
                          levelRef={voice.speechLevelRef}
                        />
                      ) : (
                        <InputBar
                          value={draft}
                          onChange={setDraft}
                          onSend={handleSend}
                          onSwitchToVoice={
                            step === 2 && !simComplete && scenarioOpen && voiceAvailable ? switchToVoice : undefined
                          }
                          switchToVoiceLabel={voiceFallback === 'denied' ? 'Retry Voice' : 'Speak Answer'}
                          // Only the Live Scenario takes a typed reply (see handleSend),
                          // and only once it has started; everywhere else the bar sits
                          // muted so it doesn't invite typing. Not gated on simThinking,
                          // so the glow doesn't flicker each turn.
                          disabled={!(step === 2 && hasSimulation && !simComplete && scenarioOpen)}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {showCelebration && (
        <LessonCompleteStrip
          moduleName={stripModuleName}
          lessonName={stripLessonName}
          lessonScorePct={stripLessonScorePct}
          runningScorePct={gamSummary?.runningScore ?? null}
          scenarioSubmitted={hasSimulation}
          weeksActive={gamSummary?.streak.weeksActive ?? null}
          onNext={
            // Offered only when the nav actually names an openable next lesson.
            // On the last lesson of a program, or before the tree has loaded,
            // there is nothing to go to and the button does not appear.
            nextLesson
              ? () => {
                  setShowCelebration(false);
                  navigate(`/app/home/mentee/level/${nextLesson.moduleId}/task/${nextLesson.id}`);
                }
              : undefined
          }
          onDone={() => {
            setShowCelebration(false);
            navigate(PATHWAY_ROUTE);
          }}
        />
      )}

      {/* L-9/L-10: outstanding items at program completion, or at the end of a
          retake pass that still leaves items. Dismissible; clears nothing. */}
      {showImproveModal && queueItems && (
        <ImprovePerformanceModal
          items={queueItems}
          onImprove={() => {
            setShowImproveModal(false);
            if (queueItems.length > 0) goToImproveItem(queueItems[0]);
          }}
          onDismiss={() => {
            setShowImproveModal(false);
            if (!improveActive) navigate(PATHWAY_ROUTE);
          }}
        />
      )}

      {/* A retake pass that cleared everything → the completion state (L-10). */}
      {showAllClear && (
        <CelebrationModal
          title="All clear!"
          subtitle="You've resolved every outstanding item in this program. Great work."
          onContinue={() => {
            setShowAllClear(false);
            navigate(PATHWAY_ROUTE);
          }}
        />
      )}

      <WhatsNewModal
        open={whatsNew.open}
        programName={lesson?.breadcrumb?.programName || selectedGroup?.name}
        whatsNew={whatsNew.learnerWhatsNew}
        onClose={whatsNew.dismiss}
      />
    </Box>
  );
};

export default ElliottLessonPage;
